// Presentational pieces shared by the Game Day Lab panels, in the Tailwind idiom of the Live Stats
// Engagement screen.

export const Card = ({ title, subtitle, right, children, className = '' }) => (
  <section className={`bg-white border border-[#E2E8F0] rounded-xl p-4 ${className}`}>
    {(title || right) && (
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          {title && <h2 className="text-[14px] font-semibold text-[#0F172A]">{title}</h2>}
          {subtitle && <p className="text-[12px] text-[#64748B] mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
    )}
    {children}
  </section>
);

const TONES = {
  live: 'bg-[#DCFCE7] text-[#166534]',
  done: 'bg-[#E2E8F0] text-[#334155]',
  idle: 'bg-[#F1F5F9] text-[#64748B]',
  warn: 'bg-[#FEF3C7] text-[#92400E]',
  info: 'bg-[#DBEAFE] text-[#1E40AF]'
};

export const Pill = ({ tone = 'idle', children }) => (
  <span
    className={`inline-flex items-center h-[22px] px-2 rounded-full text-[11px] font-semibold whitespace-nowrap ${TONES[tone] || TONES.idle}`}
  >
    {children}
  </span>
);

const VARIANTS = {
  primary: 'bg-[#0F172A] text-white border-[#0F172A] hover:bg-[#1E293B]',
  default: 'bg-white text-[#334155] border-[#CBD5E1] hover:border-[#94A3B8]',
  danger: 'bg-white text-[#B91C1C] border-[#FCA5A5] hover:border-[#B91C1C]'
};

export const Btn = ({ variant = 'default', className = '', children, ...props }) => (
  <button
    type="button"
    className={`inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-[13px] font-medium border border-solid transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${VARIANTS[variant]} ${className}`}
    {...props}
  >
    {children}
  </button>
);

export const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-[12px] font-medium text-[#475569] mb-1">
    {children}
  </label>
);

export const controlClass =
  'h-8 px-2 rounded-lg border border-[#CBD5E1] bg-white text-[13px] text-[#0F172A] focus:outline-none focus:border-[#6366F1]';

/** A labelled row of facts: `rows` is [label, value] pairs; a null value is skipped. */
export const Facts = ({ rows }) => (
  <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12px]">
    {rows
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-[#64748B]">{label}</dt>
          <dd className="text-[#0F172A] font-medium break-words">{value}</dd>
        </div>
      ))}
  </dl>
);

export const Empty = ({ children }) => <p className="text-[12px] text-[#94A3B8]">{children}</p>;

const DOTS = {
  live: 'bg-[#16A34A]',
  done: 'bg-[#64748B]',
  idle: 'bg-[#CBD5E1]',
  warn: 'bg-[#D97706]',
  info: 'bg-[#2563EB]'
};

/** One feature, both teams: a title, one line on how it behaves, then a column per side. */
export const FeatureCard = ({ title, summary, children }) => (
  <section className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
    <header className="px-4 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC]">
      <h2 className="text-[15px] font-semibold text-[#0F172A]">{title}</h2>
      {summary && <p className="text-[12px] text-[#64748B] mt-0.5 max-w-[760px]">{summary}</p>}
    </header>
    <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#E2E8F0]">
      {children}
    </div>
  </section>
);

/**
 * One side of a feature. `status` is the answer QA checks first — what a fan can do right now —
 * so it leads, large; the detail and the controls follow.
 */
export const TeamColumn = ({ teamName, side, status, children, actions }) => (
  <div className="p-4 flex flex-col min-w-0">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
      {teamName} · {side === 'home' ? 'Home' : 'Away'}
    </p>
    {status && (
      <div className="mt-1.5 flex items-start gap-2">
        <span
          aria-hidden="true"
          className={`mt-[7px] h-2.5 w-2.5 shrink-0 rounded-full ${DOTS[status.tone] || DOTS.idle}`}
        />
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-[#0F172A]">{status.label}</p>
          {status.detail && <p className="text-[12px] text-[#475569]">{status.detail}</p>}
        </div>
      </div>
    )}
    {children && <div className="mt-3 flex flex-col gap-2">{children}</div>}
    {actions && (
      <div className="mt-auto pt-3">
        <div className="pt-3 border-t border-[#F1F5F9] flex flex-wrap items-center gap-2">
          {actions}
        </div>
      </div>
    )}
  </div>
);

/** A content item (a poll, a quiz) with its own state and an optional "on the channel" flag. */
export const ItemRow = ({ title, state, onChannel, meta }) => (
  <div className="rounded-lg border border-[#E2E8F0] px-3 py-2">
    <div className="flex items-start justify-between gap-2">
      <p className="text-[13px] text-[#0F172A] min-w-0 break-words">{title}</p>
      {state && <Pill tone={state.tone}>{state.label}</Pill>}
    </div>
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[#64748B]">
      {onChannel && <Pill tone="info">On Team Channel now</Pill>}
      {meta}
    </div>
  </div>
);

export const ProgressBar = ({ value, max }) => {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between text-[12px]">
        <span className="font-semibold text-[#0F172A] tabular-nums">
          {Number(value || 0).toLocaleString('en-US')} / {Number(max || 0).toLocaleString('en-US')}{' '}
          taps
        </span>
        <span className="text-[#64748B] tabular-nums">{pct}%</span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-[#E2E8F0] overflow-hidden">
        <div
          className={`h-full rounded-full ${pct >= 100 ? 'bg-[#16A34A]' : 'bg-[#6366F1]'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};
